import React, { useState } from 'react';
import { Check, Coins, Target, Layers, Wallet, Globe2, Users, Twitter, Send, Youtube, ArrowRight, ChevronDown } from 'lucide-react';
import { presaleDetails, lionLogo, community } from '../mock';
import { useLang } from '../LangContext';

const icons = [Layers, Coins, Target, Coins, Target, Globe2];

const iconFor = (name) => {
  if (name.toLowerCase().includes('twitter')) return <Twitter className="w-4 h-4" />;
  if (name.toLowerCase().includes('telegram')) return <Send className="w-4 h-4" />;
  return <Youtube className="w-4 h-4" />;
};

const actionLabel = (name, t) => {
  if (name.toLowerCase().includes('twitter')) return t.community.follow;
  if (name.toLowerCase().includes('telegram')) return t.community.join;
  return t.community.subscribe;
};

const WhyCard = ({ title, description, isOpen, onToggle }) => (
  <div
    onClick={onToggle}
    className={`group cursor-pointer rounded-2xl border transition-all duration-300 ease-in-out
      ${isOpen
        ? 'bg-gradient-to-br from-blue-50 to-white border-blue-300 shadow-[0_12px_28px_rgba(37,99,235,0.18)] scale-[1.02]'
        : 'bg-white/70 border-slate-200 hover:bg-white hover:border-blue-200 hover:shadow-[0_8px_22px_rgba(37,99,235,0.12)] hover:scale-[1.02]'
      }`}
  >
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="flex items-center gap-3 flex-1">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300
          ${isOpen ? 'bg-blue-600 rotate-12' : 'bg-blue-100 group-hover:bg-blue-500 group-hover:rotate-12'}`}>
          <Check className={`w-4 h-4 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-blue-600 group-hover:text-white'}`} />
        </span>
        <span className={`text-[13px] font-semibold transition-colors duration-300 ${isOpen ? 'text-slate-900' : 'text-slate-700 group-hover:text-slate-900'}`}>{title}</span>
      </div>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-blue-600' : ''}`} />
    </div>
    <div
      className="overflow-hidden transition-all duration-300 ease-in-out"
      style={{ maxHeight: isOpen ? '200px' : '0px', opacity: isOpen ? 1 : 0 }}
    >
      <div className="px-4 pb-4 pt-1 text-[12.5px] text-slate-600 leading-relaxed border-t border-blue-100">
        {description}
      </div>
    </div>
  </div>
);

const InfoBlocks = () => {
  const { t } = useLang();
  const [openIdx, setOpenIdx] = useState(null);

  const whyCards = [
    { title: t.why.p1, desc: t.why.d1 },
    { title: t.why.p2, desc: t.why.d2 },
    { title: t.why.p3, desc: t.why.d3 },
    { title: t.why.p4, desc: t.why.d4 },
    { title: t.why.p5, desc: t.why.d5 },
    { title: t.why.p6, desc: t.why.d6 },
  ];

  const detailsLabels = [t.details.supply, t.details.softCap, t.details.hardCap, t.details.phases, t.details.listing, t.details.blockchain];

  return (
    <section className="relative z-10 max-w-[1400px] mx-auto px-6 mt-8 grid grid-cols-1 lg:grid-cols-[1fr_1fr_320px] gap-5 items-start">
      {/* Why Domino - Interactive cards */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-4 mb-4">
          <img
            src={lionLogo}
            alt="Domino Arena"
            className="w-16 h-16 shrink-0 object-contain drop-shadow-[0_8px_18px_rgba(30,64,175,0.3)]"
          />
          <h3 className="text-xl font-black text-slate-900 tracking-tight">{t.why.title}</h3>
        </div>
        <div className="grid grid-cols-1 gap-2">
          {whyCards.map((c, i) => (
            <WhyCard
              key={i}
              title={c.title}
              description={c.desc}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? null : i)}
            />
          ))}
        </div>
      </div>

      {/* Presale details */}
      <div className="glass-card p-6">
        <h3 className="text-xl font-black text-slate-900 tracking-tight">{t.details.title}</h3>
        <ul className="mt-4 divide-y divide-slate-100">
          {presaleDetails.map((d, i) => {
            const Icon = icons[i] || Wallet;
            return (
              <li key={i} className="flex items-center justify-between py-2.5">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-blue-600" />
                  </span>
                  <span className="text-[13px] text-slate-600 font-semibold">{detailsLabels[i]}</span>
                </div>
                <span className="text-[13px] font-extrabold text-slate-900">{d.value}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Community */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-blue-600" />
          <h3 className="text-[14px] font-black tracking-[0.14em] text-slate-900">{t.community.title}</h3>
        </div>
        <ul className="divide-y divide-slate-100">
          {community.map((c, i) => (
            <li key={i} className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full text-white flex items-center justify-center ${
                  c.name.includes('Twitter') ? 'bg-slate-900' : c.name.includes('Telegram') ? 'bg-sky-500' : 'bg-red-600'
                }`}>{iconFor(c.name)}</span>
                <span className="text-[13px] font-bold text-slate-800">{c.name}</span>
              </div>
              <a href={c.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[12px] font-extrabold text-blue-600 hover:text-blue-700">
                {actionLabel(c.name, t)} <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default InfoBlocks;
